import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { concatMap, filter, take, timer } from 'rxjs';
import { setVehicleStatusTab, vehicleStatusTabSelector } from '@store/slices/portal.slice';
import { RxUtils, Tr } from '@utils';
import { useService } from '@hooks';
import {
	bulkFleetVehicleUploadService,
	BulkUploadTask,
	BulkUploadTaskStatus,
	carListBandService,
	FleetVehicle,
	fleetVehicleService,
	Permission,
	VehicleStatus
} from '@services';
import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import { EmptyEntityPageLayout, GeneratingResultsDialog, GuardedRender, PortalPageTitle } from '@components/portal/common';
import { VehicleListViewer } from '@components/portal/vehicle-list';
import { DarkCard, EntityDeleteConfirmationDialog } from '@components/common';
import { ReactComponent as Illustration } from '@assets/illustrations/no-cars-added.svg';

const paddingX = 9;
type Entity = FleetVehicle;

const getEmptyTabContentTranslationKey = (status: VehicleStatus) => {
	const map: Record<VehicleStatus, string> = {
		[VehicleStatus.COMPLETED]: 'vehicle-list.empty-completed-tab-content',
		[VehicleStatus.IN_ERROR]: 'vehicle-list.empty-rejected-tab-content',
		[VehicleStatus.MISSING_MANDATORY_DATA]: 'vehicle-list.empty-rejected-tab-content',
		[VehicleStatus.PENDING]: 'vehicle-list.empty-pending-tab-content'
	};
	return map[status];
};

export const VehicleList: FC = () => {
	const { t } = useTranslation('portal');
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const vehicleStatusTab = useSelector(vehicleStatusTabSelector);
	const [bulkUploadInProgress, setBulkUploadInProgress] = useState(false);
	const [bulkUploadTask, setBulkUploadTask] = useState<BulkUploadTask>();
	const [searchText, setSearchText] = useState('');
	const [searchResult, setSearchResult] = useState<Entity[]>([]);
	const [deletingEntities, setDeletingEntities] = useState<Entity[]>([]);
	const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
	const { data: segments = [] } = useService(() => carListBandService.getAll());
	const { data: allEntities = [], trigger: triggerGetAll } = useService(() => fleetVehicleService.getAll(), []);
	const { data: entitiesByStatus = [], trigger: triggerGetByStatus } = useService(
		() => fleetVehicleService.getByStatus(vehicleStatusTab),
		[vehicleStatusTab]
	);
	const entities = (searchText ? searchResult : entitiesByStatus).sort(
		(entity1, entity2) => (entity1.createdAt.getTime() || 0) - (entity2.createdAt.getTime() || 0)
	);

	const onEdit = (d: Entity) => navigate(`./edit/${d.id}`);
	const onDelete = (d: Entity) => RxUtils.promisify(fleetVehicleService.delete(d), () => (triggerGetAll(), triggerGetByStatus()));
	const onDeleteMultiple = (d: Entity[]) =>
		RxUtils.promisify(fleetVehicleService.deleteMultiple(d), () => (triggerGetAll(), triggerGetByStatus()));

	useEffect(() => setSelectedEntities([]), [vehicleStatusTab]);
	useEffect(() => {
		if (searchText) {
			RxUtils.promisify(fleetVehicleService.search([vehicleStatusTab, searchText].join('/')), setSearchResult);
		} else {
			setSearchResult([]);
		}
	}, [searchText, vehicleStatusTab]);

	useEffect(() => {
		const subscription = timer(0, 5000)
			.pipe(
				concatMap(() => bulkFleetVehicleUploadService.getLatest(true)),
				filter((result) => !!result),
				take(12)
			)
			.subscribe({
				next: (result) => {
					const isSameTask = bulkUploadTask && bulkUploadTask.id === result.id;
					if ([BulkUploadTaskStatus.IN_PROGRESS, BulkUploadTaskStatus.STARTING].includes(result.status)) {
						setBulkUploadInProgress(true);
						setBulkUploadTask(undefined);
					} else {
						setBulkUploadInProgress(false);
						if (bulkUploadTask) {
							!isSameTask && setBulkUploadTask(result);
						} else {
							setBulkUploadTask(result);
						}
					}
				},
				error: () => {
					setBulkUploadInProgress(false);
					setBulkUploadTask(undefined);
				},
				complete: () => {
					setBulkUploadInProgress(false);
					setBulkUploadTask(undefined);
				}
			});

		return () => subscription.unsubscribe();
	}, [bulkUploadTask]);

	return (
		<Stack direction="column" spacing={4} sx={{ position: 'relative', height: 1.0, maxHeight: 1.0, paddingY: 5, overflow: 'hidden' }}>
			<PortalPageTitle
				paddingX={paddingX}
				title={<Tr.Portal path="vehicle-list.title" />}
				subtitle={<Tr.Portal path="vehicle-list.subtitle" />}
				displaySearchbox
				searchboxProps={{
					testId: 'vehicle-list-search-box',
					placeholder: t('vehicle-list.search-placeholder'),
					searchText,
					onSearchTextChange: setSearchText
				}}
			/>
			<GuardedRender canRender={(user) => user.hasPermission(Permission.VEHICLE_WRITE) && !user.isDriver()}>
				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', paddingX, gap: 5 }}>
					<DarkCard
						pattern="linepattern1"
						title={<Tr.Portal path="vehicle-list.add-single-vehicle-title" />}
						subtitle={<Tr.Portal path="vehicle-list.add-single-vehicle-subtitle" />}
						onClick={() => navigate('./add')}
					/>
					<DarkCard
						pattern="linepattern2"
						title={<Tr.Portal path="vehicle-list.bulk-update-title" />}
						subtitle={<Tr.Portal path="vehicle-list.bulk-update-subtitle" />}
						onClick={() => navigate('./bulk')}
					/>
				</Box>
			</GuardedRender>
			{allEntities?.length ? (
				<Stack direction="column" sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
					<Tabs value={vehicleStatusTab} onChange={(_e, val) => dispatch(setVehicleStatusTab(val))} centered>
						<Tab value={VehicleStatus.COMPLETED} label={<Tr.Portal path="vehicle-list.tab-complete" />} />
						<Tab value={VehicleStatus.PENDING} label={<Tr.Portal path="vehicle-list.tab-pending" />} />
						<Tab value={VehicleStatus.MISSING_MANDATORY_DATA} label={<Tr.Portal path="vehicle-list.tab-rejected" />} />
					</Tabs>

					{Array.isArray(entities) && entities.length ? (
						<Stack direction="column" spacing={3} sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
							<VehicleListViewer
								showResultCount={!!searchText}
								paddingX={paddingX}
								entities={entities}
								selected={selectedEntities}
								segments={segments}
								empty={<Illustration />}
								onSelectChange={setSelectedEntities}
								onEdit={onEdit}
								onDelete={(entity) => setDeletingEntities([entity])}
							/>
							{selectedEntities.length ? (
								<GuardedRender canRender={(user) => user.hasPermission(Permission.VEHICLE_WRITE) && !user.isDriver()}>
									<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
										<Button
											data-testid="delete-vehicle-modal-open-button"
											variant="contained"
											color="secondary"
											sx={{ textTransform: 'uppercase' }}
											onClick={() => setDeletingEntities(selectedEntities)}
										>
											<Tr.Portal path="delete" />
										</Button>
										<Button
											data-testid="cancel-vehicle-selection-button"
											variant="outlined"
											color="secondary"
											sx={{ textTransform: 'uppercase' }}
											onClick={() => setSelectedEntities([])}
										>
											<Tr.Portal path="cancel" />
										</Button>
									</Stack>
								</GuardedRender>
							) : null}
						</Stack>
					) : (
						<EmptyEntityPageLayout
							title={<Tr.Portal path="vehicle-list.empty-tab-title" />}
							content={<Tr.Portal path={getEmptyTabContentTranslationKey(vehicleStatusTab)} />}
						/>
					)}
				</Stack>
			) : (
				<EmptyEntityPageLayout illustration={<Illustration />} title={<Tr.Portal path="vehicle-list.no-cars-added-title" />} />
			)}
			<EntityDeleteConfirmationDialog
				open={!!deletingEntities?.length}
				content={<Tr.Portal path="vehicle-list.delete-confirmation-message" />}
				buttonProps={{ 'data-testid': 'vehicle-list-delete-confirmation-button' }}
				onClose={() => setDeletingEntities([])}
				onAccept={() => {
					deletingEntities.length > 1 ? onDeleteMultiple?.(deletingEntities) : onDelete?.(deletingEntities[0]);
					setDeletingEntities([]);
					setSelectedEntities([]);
				}}
			>
				{deletingEntities.map((e) => `"${e.plateNumber || e.id}"`).join(', ')}
			</EntityDeleteConfirmationDialog>
			<GeneratingResultsDialog
				hideCloseIcon
				open={bulkUploadInProgress}
				contentTitle={<Tr.Portal path="generating-result-dialog.content-vehicle-bulk-upload" />}
			/>
		</Stack>
	);
};
